import Alert from '~/components/Alert';
import Submit from '~/components/button/Submit';
import Form from '~/components/Form';
import Input from '~/components/form-controls/Input';
import LineItem from '~/components/LineItem';
import {
  editActionTypeFormKey,
  QUANTITY_INPUT_PREFIX,
  QUANTITY_ORIGINAL_PREFIX,
  useLoaderData as useStageLoaderData,
} from './orderEditStage';

type LoaderData = ReturnType<typeof useStageLoaderData>;
type ModifyQuantityData = Extract<LoaderData, { editType: 'MODIFY_QUANTITY' }>;

function boundsFor(
  mode: 'ADD' | 'REMOVE' | 'BOTH',
  cap: number,
  currentQuantity: number,
) {
  const baseMin = Math.max(0, currentQuantity - cap);
  const baseMax = currentQuantity + cap;
  if (mode === 'ADD') return { min: currentQuantity, max: baseMax };
  if (mode === 'REMOVE') return { min: baseMin, max: currentQuantity };
  return { min: baseMin, max: baseMax };
}

export function ModifyQuantityForm({ data }: { data: ModifyQuantityData }) {
  const { action } = data;
  const mode = action.quantityModificationType;
  const cap = action.quantityModificationValue;

  // don't allow editing of corso line items
  const lineItems = data.lineItems.filter((li) => !li.isCorsoProduct);

  return (
    <Form name="modifyQuantityForm">
      <input type="hidden" name={editActionTypeFormKey} value={data.editType} />

      {mode === 'ADD' && (
        <Alert variant="info" title="Adding items only">
          You may increase quantities up to {cap} per line.
        </Alert>
      )}
      {mode === 'REMOVE' && (
        <Alert variant="info" title="Removing items only">
          You may reduce quantities by up to {cap} per line.
        </Alert>
      )}

      <ul className="flex flex-col gap-3">
        {lineItems.map((li) => {
          const { min, max } = boundsFor(mode, cap, li.currentQuantity);
          const disabled = min === max;
          const rangeUnderflowMessage =
            mode === 'ADD'
              ? `You can’t reduce below the current quantity of ${li.currentQuantity}.`
              : `You can remove at most ${cap} (minimum allowed: ${min}).`;
          const rangeOverflowMessage =
            mode === 'REMOVE'
              ? `You can’t exceed the current quantity of ${li.currentQuantity}.`
              : `You can add at most ${cap} more (maximum allowed: ${max}).`;
          return (
            <li key={li.id}>
              <LineItem
                variant="small"
                name={li.name ?? ''}
                imgSrc={li.imgUrl}
                quantity={li.currentQuantity}
                options={li.optionsFromPlatform}
              >
                <div className="flex justify-end">
                  <input
                    type="hidden"
                    name={`${QUANTITY_ORIGINAL_PREFIX}${li.id}`}
                    value={li.currentQuantity}
                  />
                  <Input
                    id={`qty-${li.id}`}
                    label="Quantity"
                    hideLabel
                    required
                    name={`${QUANTITY_INPUT_PREFIX}${li.id}`}
                    type="number"
                    defaultValue={li.currentQuantity}
                    min={`${min}`}
                    max={`${max}`}
                    disabled={disabled}
                    className="w-16 shrink-0"
                    validationMessages={{
                      valueMissing: 'Please enter a quantity.',
                      badInput: 'Please enter a whole number.',
                      stepMismatch: 'Please enter a whole number.',
                      rangeUnderflow: rangeUnderflowMessage,
                      rangeOverflow: rangeOverflowMessage,
                    }}
                  />
                </div>
              </LineItem>
            </li>
          );
        })}
      </ul>

      <Submit>Continue</Submit>
    </Form>
  );
}
